/**
 * Matchmaking Ranking — orchestration côté client.
 *
 * Réutilise les endpoints web existants (aucun nouveau backend) :
 *   GET  /ranking/available-users        → candidats (can_challenge / can_amical)
 *   POST /ranking/toggle-status          → activer sa disponibilité si besoin
 *   POST /ranking/challenge              → défier un adversaire
 *   GET  /ranking/check-challenge        → suivre la réponse
 *   POST /ranking/match/{id}/cancel      → annuler un défi resté sans réponse
 *
 * Déroulé : on défie d'abord les adversaires jouables en RANKING (les plus proches
 * en ELO), puis les autres en AMICAL. Chaque défi laisse 1 min à l'adversaire ;
 * sans réponse on annule et on passe au suivant. File épuisée → on ré-interroge
 * (au cas où quelqu'un se connecte) jusqu'à 5 min, puis « pas d'adversaire ».
 *
 * Le bouton déclencheur vit sur /ranking/dashboard ET dans la sidebar ranking ;
 * le module est chargé globalement et exposé via window.startRankingMatchmaking().
 */
(function () {
    const TOTAL_TIMEOUT_MS = 5 * 60 * 1000; // 5 min : abandon global
    const ATTEMPT_TIMEOUT_MS = 60 * 1000;   // 1 min par adversaire
    const POLL_MS = 3000;                   // fréquence de check-challenge
    const RETRY_MS = 5000;                  // ré-interrogation quand la file est vide

    const RankingMatchmaking = {
        running: false,
        tried: null,
        searchStartedAt: 0,
        announced: false,
        currentMatchId: null,
        currentOpponent: null,
        attemptDeadline: 0,
        pollTimer: null,
        retryTimer: null,
        overlay: null,
        logEl: null,
        footerEl: null,
        lastLine: '',

        csrf() {
            const el = document.querySelector('meta[name="csrf-token"]');
            return el ? el.getAttribute('content') : '';
        },

        myRanking() {
            const el = document.querySelector('meta[name="master-ranking"]');
            return el ? (parseInt(el.content, 10) || 1000) : 1000;
        },

        get(url) {
            return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then(r => r.json());
        },

        post(url, body) {
            return fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': this.csrf() },
                body: body ? JSON.stringify(body) : undefined,
            }).then(r => r.json());
        },

        // ========================= Cycle de vie =========================

        start() {
            if (this.running) { this.showPopup(); return; }

            this.get('/ranking/available-users')
                .then(data => {
                    if (data && data.active_match) {
                        this.showPopup();
                        this.concludeActiveMatch(data.active_match);
                        return;
                    }
                    this.running = true;
                    this.tried = new Set();
                    this.searchStartedAt = Date.now();
                    this.announced = false;
                    this.showPopup();
                    this.ensureStatusOn().then(() => this.consume(data));
                })
                .catch(() => {
                    this.showPopup();
                    this.conclude('error', "Impossible de lancer la recherche pour le moment.");
                });
        },

        /** Active la disponibilité ranking si elle est coupée (sinon défier échoue). */
        ensureStatusOn() {
            const meta = document.querySelector('meta[name="ranking-status"]');
            const current = meta ? meta.content : 'off';
            if (current !== 'off') return Promise.resolve();

            return this.post('/ranking/toggle-status')
                .then(data => {
                    if (data && data.success) {
                        if (meta) meta.content = data.ranking_status;
                        window.dispatchEvent(new CustomEvent('ranking-status-changed', { detail: { status: data.ranking_status } }));
                    }
                })
                .catch(() => {});
        },

        timedOut() {
            return (Date.now() - this.searchStartedAt) >= TOTAL_TIMEOUT_MS;
        },

        /** Recharge la liste puis relance une tentative. */
        searchLoop() {
            if (!this.running) return;
            if (this.timedOut()) { this.concludeNoOpponent(); return; }
            this.get('/ranking/available-users')
                .then(data => this.consume(data))
                .catch(() => { this.retryTimer = setTimeout(() => this.searchLoop(), RETRY_MS); });
        },

        /** Construit la file (ranking d'abord, puis amical) et défie le premier candidat. */
        consume(data) {
            if (!this.running) return;
            if (this.timedOut()) { this.concludeNoOpponent(); return; }

            const users = (data && data.users) || [];
            const queue = this.buildQueue(users);

            if (!this.announced) {
                this.announced = true;
                const rankingCount = users.filter(u => u.can_challenge).length;
                const amicalCount = users.filter(u => u.can_amical && !u.can_challenge).length;
                this.progress(`${rankingCount} adversaire(s) en ranking, ${amicalCount} en amical.`);
            }

            if (queue.length === 0) {
                this.progress("En attente qu'un adversaire se connecte…");
                this.retryTimer = setTimeout(() => this.searchLoop(), RETRY_MS);
                return;
            }

            this.challengeCandidate(queue[0]);
        },

        /**
         * Candidats ordonnés : jouables en ranking d'abord (les plus proches en ELO),
         * puis les autres en amical. On exclut ceux déjà sollicités dans la session.
         */
        buildQueue(users) {
            const my = this.myRanking();
            const byDistance = (a, b) => Math.abs(a.master_ranking - my) - Math.abs(b.master_ranking - my);

            const ranking = users
                .filter(u => u.can_challenge)
                .map(u => Object.assign({}, u, { mode: 'ranking' }))
                .sort(byDistance);

            const amical = users
                .filter(u => u.can_amical && !u.can_challenge)
                .map(u => Object.assign({}, u, { mode: 'amical' }))
                .sort(byDistance);

            return ranking.concat(amical).filter(u => !this.tried.has(u.id));
        },

        challengeCandidate(candidate) {
            const isFriendly = candidate.mode === 'amical';
            this.currentOpponent = candidate;
            this.tried.add(candidate.id);
            this.progress(`⚔️ Défi ${isFriendly ? 'amical' : 'ranking'} envoyé à ${candidate.name}… en attente de sa réponse.`);

            this.post('/ranking/challenge', { opponent_id: candidate.id, is_friendly: isFriendly })
                .then(data => {
                    if (!this.running) return;
                    if (!data || !data.success) {
                        // Devenu indisponible entre-temps : on passe au suivant.
                        this.progress(`${candidate.name} n'est plus disponible.`);
                        this.searchLoop();
                        return;
                    }
                    this.currentMatchId = data.ranking_match_id;
                    this.attemptDeadline = Date.now() + ATTEMPT_TIMEOUT_MS;
                    this.startPoll();
                })
                .catch(() => { if (this.running) this.searchLoop(); });
        },

        startPoll() {
            this.stopPoll();
            this.pollTimer = setInterval(() => this.pollAttempt(), POLL_MS);
        },

        stopPoll() {
            if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null; }
        },

        pollAttempt() {
            if (!this.running || !this.currentMatchId) { this.stopPoll(); return; }

            if (this.timedOut()) {
                this.stopPoll();
                this.cancelCurrent();
                this.concludeNoOpponent();
                return;
            }

            const name = (this.currentOpponent && this.currentOpponent.name) || 'Adversaire';

            this.get('/ranking/check-challenge?ranking_match_id=' + this.currentMatchId)
                .then(data => {
                    if (!this.running || !data || !data.success) return;

                    if (data.status === 'accepted' && data.redirect) {
                        this.stopPoll();
                        this.running = false;
                        this.progress(`✅ ${name} a accepté ! Redirection…`);
                        this.setState('matched');
                        setTimeout(() => { window.location.href = data.redirect; }, 1200);
                        return;
                    }

                    if (data.status === 'refused') {
                        this.stopPoll();
                        this.progress(`${name} a refusé.`);
                        this.clearAttempt();
                        this.searchLoop();
                        return;
                    }

                    if (data.status === 'waiting') {
                        if (Date.now() >= this.attemptDeadline) {
                            this.stopPoll();
                            this.progress(`${name} n'a pas répondu.`);
                            this.cancelCurrent();
                            this.searchLoop();
                        }
                        return;
                    }

                    // délai proposé/en attente/expiré : on ne bloque pas la recherche.
                    this.stopPoll();
                    this.progress(`${name} n'est pas dispo tout de suite — on cherche ailleurs.`);
                    this.cancelCurrent();
                    this.searchLoop();
                })
                .catch(() => {});
        },

        /** Annule le défi en cours (timeout, délai, ou abandon) côté serveur. */
        cancelCurrent() {
            const id = this.currentMatchId;
            this.clearAttempt();
            if (id) this.post('/ranking/match/' + id + '/cancel').catch(() => {});
        },

        clearAttempt() {
            this.currentMatchId = null;
            this.currentOpponent = null;
            this.attemptDeadline = 0;
        },

        stopAllTimers() {
            this.stopPoll();
            if (this.retryTimer) { clearTimeout(this.retryTimer); this.retryTimer = null; }
        },

        // ========================= Conclusions =========================

        concludeNoOpponent() {
            this.stopAllTimers();
            this.cancelCurrent();
            this.running = false;
            this.conclude('empty', "Aucun adversaire disponible pour le moment. Réessaie un peu plus tard.");
        },

        concludeActiveMatch(match) {
            this.running = false;
            this.conclude('active', `Tu as déjà un match ranking en cours contre ${match.opponent_name}.`, match.url);
        },

        /** Abandon manuel via le bouton « Annuler la recherche ». */
        cancel() {
            this.stopAllTimers();
            this.cancelCurrent();
            this.running = false;
            this.closePopup();
        },

        // ========================= Popup =========================

        showPopup() {
            if (this.overlay) { this.overlay.classList.add('is-visible'); return; }

            const overlay = document.createElement('div');
            overlay.className = 'ranking-popup-overlay matchmaking-overlay is-visible';
            overlay.innerHTML =
                '<div class="ranking-popup matchmaking-popup">' +
                    '<div class="ranking-popup-header matchmaking-header">' +
                        '<span class="matchmaking-spinner" aria-hidden="true"></span>' +
                        '<h3>Recherche d\'un adversaire…</h3>' +
                    '</div>' +
                    '<div class="ranking-popup-body">' +
                        '<div class="matchmaking-log" role="log" aria-live="polite"></div>' +
                    '</div>' +
                    '<div class="ranking-popup-footer matchmaking-footer">' +
                        '<button type="button" class="ranking-popup-btn ranking-popup-btn--cancel" data-mm="cancel">' +
                            '<span class="material-icons">close</span> Annuler la recherche' +
                        '</button>' +
                    '</div>' +
                '</div>';

            document.body.appendChild(overlay);
            this.overlay = overlay;
            this.logEl = overlay.querySelector('.matchmaking-log');
            this.footerEl = overlay.querySelector('.matchmaking-footer');
            this.lastLine = '';

            overlay.querySelector('[data-mm="cancel"]').addEventListener('click', () => this.cancel());
            overlay.addEventListener('click', (e) => { if (e.target === overlay && !this.running) this.closePopup(); });
        },

        closePopup() {
            if (this.overlay) { this.overlay.remove(); this.overlay = null; this.logEl = null; this.footerEl = null; }
        },

        /** Ajoute une ligne au journal (dédup consécutive) — textContent = pas d'injection. */
        progress(line) {
            if (!this.logEl || line === this.lastLine) return;
            this.lastLine = line;
            const row = document.createElement('p');
            row.className = 'matchmaking-line';
            row.textContent = line;
            this.logEl.appendChild(row);
            this.logEl.scrollTop = this.logEl.scrollHeight;
        },

        /** Bascule l'état visuel (spinner/titre) selon l'issue. */
        setState(state) {
            if (!this.overlay) return;
            const header = this.overlay.querySelector('.matchmaking-header');
            const spinner = this.overlay.querySelector('.matchmaking-spinner');
            const title = this.overlay.querySelector('.matchmaking-header h3');
            if (spinner && state !== 'searching') spinner.remove();
            if (header) header.classList.toggle('is-done', state !== 'searching' && state !== 'matched');
            if (title) {
                const titles = {
                    matched: 'Adversaire trouvé !',
                    empty: 'Recherche terminée',
                    active: 'Match en cours',
                    error: 'Recherche indisponible',
                };
                if (titles[state]) title.textContent = titles[state];
            }
        },

        /** Termine la recherche et remplace le pied par un bouton de fermeture (+ lien optionnel). */
        conclude(state, message, url) {
            this.showPopup();
            this.setState(state);
            this.progress(message);
            if (!this.footerEl) return;
            this.footerEl.innerHTML = '';
            if (url) {
                const go = document.createElement('a');
                go.className = 'ranking-popup-btn ranking-popup-btn--go';
                go.href = url;
                go.innerHTML = '<span class="material-icons">open_in_new</span> Voir le match';
                this.footerEl.appendChild(go);
            }
            const close = document.createElement('button');
            close.type = 'button';
            close.className = 'ranking-popup-btn ranking-popup-btn--close';
            close.textContent = 'Fermer';
            close.addEventListener('click', () => this.closePopup());
            this.footerEl.appendChild(close);
        },
    };

    window.startRankingMatchmaking = function () { RankingMatchmaking.start(); };
})();
